import {
  ExpertProfileSelectDto,
  MissionAssessmentDraftSelectDto,
  MissionAssessmentRequestSelectDto,
  MissionAssessmentRequestStatus,
  MissionAssessmentSelectDto,
} from '@@types/api.types';
import { TooltipComponent } from '@components/core/tooltip.component';
import { NumberBadge } from '@components/shared/number-badge.component';
import { Tile } from '@components/shared/tile.component';
import { Label } from '@components/ui/label';
import { Switch } from '@components/ui/switch';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { EmptyStateContainer } from '../../../shared/empty-state-container.component';
import { MissionAssessmentCard } from './mission-assessment-card.component';
import { MissionAssessmentRequestCard } from './mission-assessment-request-card.component';

export const calculateAggregatedCompletedAssessments = (
  completedAssessments: MissionAssessmentSelectDto[],
): Array<MissionAssessmentSelectDto & { assessmentCount: number }> => {
  if (!completedAssessments || completedAssessments.length === 0) {
    return [];
  }

  const completedByTechId = new Map<string, MissionAssessmentSelectDto[]>();

  completedAssessments.forEach((assessment) => {
    const techId = assessment.technology.id;
    if (!completedByTechId.has(techId)) {
      completedByTechId.set(techId, []);
    }

    const assessmentsForTech = completedByTechId.get(techId);
    assessmentsForTech?.push(assessment);
  });

  const aggregatedResult: Array<MissionAssessmentSelectDto & { assessmentCount: number }> = [];
  completedByTechId.forEach((assessmentsInGroup) => {
    if (assessmentsInGroup.length > 0) {
      const sortedAssessments = [...assessmentsInGroup].sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      );
      const mostRecentAssessment = sortedAssessments[0];
      aggregatedResult.push({
        ...mostRecentAssessment,
        assessmentCount: assessmentsInGroup.length,
      });
    }
  });

  aggregatedResult.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  return aggregatedResult;
};

export const deduplicateDraftsByRequest = (
  drafts: MissionAssessmentDraftSelectDto[],
): MissionAssessmentDraftSelectDto[] => {
  if (!drafts || drafts.length === 0) {
    return [];
  }

  const draftsByRequestId = new Map<string, MissionAssessmentDraftSelectDto[]>();

  drafts.forEach((draft) => {
    const requestId = draft.request?.id;
    if (!requestId) {
      return;
    }

    if (!draftsByRequestId.has(requestId)) {
      draftsByRequestId.set(requestId, []);
    }

    const draftsForRequest = draftsByRequestId.get(requestId);
    draftsForRequest?.push(draft);
  });

  const deduplicatedResult: MissionAssessmentDraftSelectDto[] = [];
  draftsByRequestId.forEach((draftsInGroup) => {
    if (draftsInGroup.length > 0) {
      const sortedDrafts = [...draftsInGroup].sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      );
      const mostRecentDraft = sortedDrafts[0];
      deduplicatedResult.push(mostRecentDraft);
    }
  });

  deduplicatedResult.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  return deduplicatedResult;
};

export type MissionAssessmentSection = 'available' | 'completed' | 'draft';

export type MissionAssessmentListProps = {
  availableRequests: MissionAssessmentRequestSelectDto[];
  completedAssessments: Array<MissionAssessmentSelectDto & { assessmentCount: number }>;
  draftAssessments: MissionAssessmentDraftSelectDto[];
  exactMatchOnly: boolean;
  hasBackendRequests: boolean;
  isLoadingCompleted: boolean;
  isLoadingDrafts: boolean;
  isLoadingRequests: boolean;
  layout?: 'default' | 'compact';
  maxItemsPerSection?: number;
  onExactMatchChange: (value: boolean) => void;
  sections: MissionAssessmentSection[];
  userProfile?: ExpertProfileSelectDto;
};

export const MissionAssessmentList: React.FC<MissionAssessmentListProps> = (props) => {
  const {
    availableRequests,
    completedAssessments,
    draftAssessments,
    exactMatchOnly,
    hasBackendRequests,
    isLoadingCompleted,
    isLoadingDrafts,
    isLoadingRequests,
    layout = 'default',
    maxItemsPerSection,
    onExactMatchChange,
    sections,
  } = props;
  const navigate = useNavigate();

  const shouldShowSection = (section: MissionAssessmentSection) => sections.includes(section);

  const hasDraftAssessments = draftAssessments.length > 0;
  const hasCompletedAssessments = completedAssessments.length > 0;

  return (
    <div className="flex flex-col gap-4">
      {shouldShowSection('available') && (
        <Tile
          emptyState={{
            title: 'No Mission Assessment Requests',
          }}
          header={{
            button:
              maxItemsPerSection && availableRequests.length > maxItemsPerSection
                ? {
                    onClick: () => navigate('/assessments'),
                    text: 'View all',
                  }
                : undefined,
            title: (
              <div className="flex items-center justify-between w-full">
                <SectionTitle
                  quantity={availableRequests.length}
                  title="Available"
                />
                <div className="flex items-center gap-2">
                  <Switch
                    checked={exactMatchOnly}
                    id="exact-match-toggle"
                    onCheckedChange={onExactMatchChange}
                  />
                  <Label
                    className="text-sm text-gray-600 cursor-pointer whitespace-nowrap"
                    htmlFor="exact-match-toggle"
                  >
                    Only show exact matches
                  </Label>
                  <TooltipComponent
                    className="text-xs font-normal"
                    side="bottom"
                  >
                    When enabled, only mission assessment requests that match your expert profile's country of service
                    and military service will be shown. Requests without specific criteria will always be displayed.
                  </TooltipComponent>
                </div>
              </div>
            ),
          }}
          isEmpty={!hasBackendRequests}
          isLoading={isLoadingRequests}
          loadingState={{ message: 'Loading available requests...' }}
        >
          {availableRequests.length > 0 ? (
            <div className="flex flex-col gap-2 bg-gray-100 p-2 rounded-lg">
              {availableRequests.map((request) => (
                <MissionAssessmentRequestCard
                  key={request.id}
                  missionAssessmentRequest={request}
                />
              ))}
            </div>
          ) : (
            <EmptyStateContainer title="No Exact Matches Found" />
          )}
        </Tile>
      )}

      {shouldShowSection('draft') && hasDraftAssessments && (
        <Tile
          emptyState={{ title: 'No Mission Assessments in Draft' }}
          header={{
            button:
              maxItemsPerSection && draftAssessments.length > maxItemsPerSection
                ? {
                    onClick: () => navigate('/assessments'),
                    text: 'View all',
                  }
                : undefined,
            title: (
              <SectionTitle
                quantity={draftAssessments.length}
                title="Draft"
              />
            ),
          }}
          isEmpty={!hasDraftAssessments}
          isLoading={isLoadingDrafts}
          loadingState={{ message: 'Loading draft assessments...' }}
        >
          <div className="flex flex-col gap-2 bg-gray-100 p-2 rounded-lg">
            {draftAssessments.map((draft) => (
              <MissionAssessmentCard
                assessmentId={draft.id}
                isDraft
                isRequestWithdrawn={draft.request?.status === MissionAssessmentRequestStatus.CANCELLED}
                key={draft.id}
                layout={layout}
                request={draft.request}
                technology={draft.technology}
                updatedAt={draft.updatedAt}
              />
            ))}
          </div>
        </Tile>
      )}

      {shouldShowSection('completed') && hasCompletedAssessments && (
        <Tile
          emptyState={{ title: 'No Completed Mission Assessments' }}
          header={{
            button:
              maxItemsPerSection && completedAssessments.length > maxItemsPerSection
                ? {
                    onClick: () => navigate('/assessments'),
                    text: 'View all',
                  }
                : undefined,
            title: (
              <SectionTitle
                quantity={completedAssessments.length}
                title="Completed"
              />
            ),
          }}
          isEmpty={!hasCompletedAssessments}
          isLoading={isLoadingCompleted}
          loadingState={{ message: 'Loading completed assessments...' }}
        >
          <div className="flex flex-col gap-2 bg-gray-100 p-2 rounded-lg">
            {completedAssessments.map((assessment) => (
              <MissionAssessmentCard
                accessRestricted={assessment.accessRestricted}
                accessRestrictedMessage={assessment.accessRestrictedMessage}
                assessmentCount={assessment.assessmentCount}
                assessmentId={assessment.id}
                isDraft={false}
                isRequestWithdrawn={assessment.request?.status === MissionAssessmentRequestStatus.CANCELLED}
                key={assessment.id}
                layout={layout}
                request={assessment.request}
                technology={assessment.technology}
                updatedAt={assessment.updatedAt}
              />
            ))}
          </div>
        </Tile>
      )}
    </div>
  );
};

const SectionTitle: React.FC<{ quantity: number; title: string }> = ({ title, quantity }) => {
  return (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center gap-1">
        <NumberBadge value={quantity} />
        <h3 className="text-base text-gray-700">{title}</h3>
      </div>
    </div>
  );
};
